// ==================== STALKER SOUND ENGINE ====================
// Procedural audio using Web Audio API - no external files needed

export type SoundId =
  | 'ambient_wind'
  | 'ambient_drone'
  | 'ambient_drip'
  | 'ambient_creak'
  | 'search_rustle'
  | 'search_empty'
  | 'found_common'
  | 'found_uncommon'
  | 'found_rare'
  | 'found_legendary'
  | 'use_medical'
  | 'use_food'
  | 'use_protection'
  | 'use_artifact'
  | 'use_equip'
  | 'door_open'
  | 'door_close'
  | 'footstep'
  | 'geiger_click'
  | 'geiger_high'
  | 'ui_click'
  | 'ui_hover'
  | 'death'
  | 'heartbeat'
  | 'breathing'
  | 'zone_hum'
  | 'ui_error'

/**
 * Звуковые пресеты для различных типов локаций.
 * Определяют фоновые звуки и частоту случайных событий (капли, скрипы).
 */
export enum SoundPreset {
  /** Открытое пространство: усиленный ветер, минимум скрипов */
  WIND = 'WIND',
  /** Жилые помещения: умеренные скрипы мебели и пола */
  HOUSE = 'HOUSE',
  /** Кухня: частая капель воды, металлические звуки */
  DRIP = 'DRIP',
  /** Ванная: глухая капель с эхом, замкнутое пространство */
  ECHO_DRIP = 'ECHO_DRIP',
  /** Больница: высокочастотный гул оборудования, стерильная тишина */
  MED_HUM = 'MED_HUM',
  /** Промзона/Завод: тяжелый скрежет металла, низкочастотный гул */
  METAL = 'METAL',
  /** Церковь: глубокий резонанс, потусторонние эхо */
  RESONANCE = 'RESONANCE',
  /** Бункер: шум вентиляции, статическое электричество */
  VENTILATION = 'VENTILATION',
  /** Военный объект: электрический треск, гул трансформаторов */
  ELECTRIC = 'ELECTRIC',
  /** Коридор: повышенная частота скрипов, эффект сквозняка */
  CREAK = 'CREAK',
}

class StalkerSoundEngine {
  private ctx: AudioContext | null = null
  private masterGain: GainNode | null = null
  private ambientGain: GainNode | null = null
  private sfxGain: GainNode | null = null
  private presetGain: GainNode | null = null
  private activeNodes: AudioNode[] = []
  private ambientNodes: AudioNode[] = []
  private presetNodes: AudioNode[] = []
  private _muted: boolean = false
  private _volume: number = 0.7
  private initialized: boolean = false
  private currentPreset: SoundPreset = SoundPreset.WIND
  private ambientInterval: ReturnType<typeof setInterval> | null = null
  private creakTimeout: ReturnType<typeof setTimeout> | null = null
  private dripTimeout: ReturnType<typeof setTimeout> | null = null
  private geigerInterval: ReturnType<typeof setInterval> | null = null
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null

  get muted() { return this._muted }
  get volume() { return this._volume }

  init() {
    if (this.initialized) return
    try {
      this.ctx = new AudioContext()
      this.masterGain = this.ctx.createGain()
      this.masterGain.gain.value = this._volume
      this.masterGain.connect(this.ctx.destination)

      this.ambientGain = this.ctx.createGain()
      this.ambientGain.gain.value = 0.3
      this.ambientGain.connect(this.masterGain)

      this.presetGain = this.ctx.createGain()
      this.presetGain.gain.value = 0.3
      this.presetGain.connect(this.masterGain)

      this.sfxGain = this.ctx.createGain()
      this.sfxGain.gain.value = 0.7
      this.sfxGain.connect(this.masterGain)

      this.initialized = true
    } catch (e) {
      console.warn('AudioContext not available:', e)
    }
  }

  resume() {
    if (this.ctx?.state === 'suspended') {
      this.ctx.resume()
    }
  }

  setVolume(v: number) {
    this._volume = Math.max(0, Math.min(1, v))
    if (this.masterGain) this.masterGain.gain.value = this._volume
  }

  toggleMute(): boolean {
    this._muted = !this._muted
    if (this.masterGain) {
      this.masterGain.gain.value = this._muted ? 0 : this._volume
    }
    return this._muted
  }

  // ========== LOW-LEVEL HELPERS ==========

  private noise(duration: number, gain: number = 0.1, dest?: AudioNode): OscillatorNode[] {
    if (!this.ctx || !dest) return []
    const bufferSize = this.ctx.sampleRate * duration
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate)
    const data = buffer.getChannelData(0)
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * gain
    }
    const source = this.ctx.createBufferSource()
    source.buffer = buffer
    source.connect(dest)
    return []
  }

  private playTone(freq: number, duration: number, gain: number = 0.1, type: OscillatorType = 'sine', dest?: AudioNode | null) {
    if (!this.ctx || !dest) return
    const osc = this.ctx.createOscillator()
    const g = this.ctx.createGain()
    osc.type = type
    osc.frequency.value = freq
    g.gain.setValueAtTime(gain, this.ctx.currentTime)
    g.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + duration)
    osc.connect(g)
    g.connect(dest)
    osc.start()
    osc.stop(this.ctx.currentTime + duration)
  }

  private playNoiseBurst(duration: number, gain: number = 0.1, dest?: AudioNode | null, filterFreq: number = 2000) {
    if (!this.ctx || !dest) return
    const bufferSize = Math.floor(this.ctx.sampleRate * duration)
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate)
    const data = buffer.getChannelData(0)
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1
    }
    const source = this.ctx.createBufferSource()
    source.buffer = buffer

    const filter = this.ctx.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.value = filterFreq
    filter.Q.value = 1

    const g = this.ctx.createGain()
    g.gain.setValueAtTime(gain, this.ctx.currentTime)
    g.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + duration)

    source.connect(filter)
    filter.connect(g)
    g.connect(dest)
    source.start()
    source.stop(this.ctx.currentTime + duration)
  }

  // ========== AMBIENT SOUNDS ==========

  startAmbient() {
    if (!this.ctx || !this.ambientGain) return
    this.stopAmbient()

    // Wind drone - low continuous rumble
    const windOsc = this.ctx.createOscillator()
    const windGain = this.ctx.createGain()
    const windFilter = this.ctx.createBiquadFilter()
    windOsc.type = 'sawtooth'
    windOsc.frequency.value = 55 + Math.random() * 20
    windFilter.type = 'lowpass'
    windFilter.frequency.value = 200
    windGain.gain.value = 0.06
    windOsc.connect(windFilter)
    windFilter.connect(windGain)
    windGain.connect(this.ambientGain)
    windOsc.start()
    this.ambientNodes.push(windOsc)

    // Second drone - slightly detuned for thickness
    const drone2 = this.ctx.createOscillator()
    const drone2Gain = this.ctx.createGain()
    const drone2Filter = this.ctx.createBiquadFilter()
    drone2.type = 'sawtooth'
    drone2.frequency.value = 58 + Math.random() * 15
    drone2Filter.type = 'lowpass'
    drone2Filter.frequency.value = 150
    drone2Gain.gain.value = 0.04
    drone2.connect(drone2Filter)
    drone2Filter.connect(drone2Gain)
    drone2Gain.connect(this.ambientGain)
    drone2.start()
    this.ambientNodes.push(drone2)

    // Sub-bass rumble
    const subOsc = this.ctx.createOscillator()
    const subGain = this.ctx.createGain()
    subOsc.type = 'sine'
    subOsc.frequency.value = 30 + Math.random() * 10
    subGain.gain.value = 0.03
    subOsc.connect(subGain)
    subGain.connect(this.ambientGain)
    subOsc.start()
    this.ambientNodes.push(subOsc)

    // LFO for wind modulation
    const lfo = this.ctx.createOscillator()
    const lfoGain = this.ctx.createGain()
    lfo.type = 'sine'
    lfo.frequency.value = 0.15
    lfoGain.gain.value = 0.03
    lfo.connect(lfoGain)
    lfoGain.connect(windGain.gain)
    lfo.start()
    this.ambientNodes.push(lfo)

    // Random creaking sounds
    this.scheduleCreak()
    // Random dripping sounds
    this.scheduleDrip()
  }

  /**
   * Применяет звуковой пресет для текущей локации.
   * Динамически изменяет параметры фоновых звуков.
   */
  applyPreset(preset: SoundPreset) {
    if (!this.ctx || !this.initialized) return
    this.currentPreset = preset

    // Сбрасываем текущие таймауты случайных звуков, чтобы они перепланировались с новыми интервалами
    if (this.creakTimeout) {
      clearTimeout(this.creakTimeout)
      this.scheduleCreak()
    }
    if (this.dripTimeout) {
      clearTimeout(this.dripTimeout)
      this.scheduleDrip()
    }

    // Дополнительные слои звука для пресетов
    this.updatePresetLayers()
  }

  private updatePresetLayers() {
    if (!this.ctx || !this.presetGain) return

    // Останавливаем старые ноды пресета
    this.presetNodes.forEach(node => {
      try {
        if (node instanceof OscillatorNode || node instanceof AudioBufferSourceNode) {
          node.stop()
        }
      } catch (_) { /* already stopped */ }
      try { node.disconnect() } catch (_) { /* already disconnected */ }
    })
    this.presetNodes = []

    // Добавляем специфические звуки для пресета
    switch (this.currentPreset) {
      case SoundPreset.WIND:
        // Усиливаем ветер
        this.addHumLayer(120, 'sine', 0.05, 400);
        break;

      case SoundPreset.MED_HUM:
        // Высокочастотный гул оборудования
        this.addHumLayer(15000, 'sine', 0.01, 16000);
        this.addHumLayer(50, 'sine', 0.02, 100); // Низкий гул сети
        break;

      case SoundPreset.METAL:
      case SoundPreset.ELECTRIC:
        // Гул трансформатора
        this.addHumLayer(50, 'sawtooth', 0.02, 120);
        this.addHumLayer(100, 'sawtooth', 0.01, 200);
        break;

      case SoundPreset.VENTILATION:
        // Шум вентиляции (белый шум с фильтром)
        this.addNoiseLayer(0.02, 800, 200);
        break;

      case SoundPreset.RESONANCE:
        // Глубокий резонанс
        this.addHumLayer(40, 'sine', 0.06, 80);
        break;
    }
  }

  private addHumLayer(freq: number, type: OscillatorType, gain: number, filterFreq: number) {
    if (!this.ctx || !this.presetGain) return
    const osc = this.ctx.createOscillator()
    const g = this.ctx.createGain()
    const filter = this.ctx.createBiquadFilter()

    osc.type = type
    osc.frequency.value = freq
    filter.type = 'lowpass'
    filter.frequency.value = filterFreq
    g.gain.value = gain

    osc.connect(filter)
    filter.connect(g)
    g.connect(this.presetGain)
    osc.start()
    this.presetNodes.push(osc)
  }

  private addNoiseLayer(gain: number, freq: number, q: number) {
    if (!this.ctx || !this.presetGain) return

    const bufferSize = 2 * this.ctx.sampleRate;
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const output = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
        output[i] = Math.random() * 2 - 1;
    }

    const whiteNoise = this.ctx.createBufferSource();
    whiteNoise.buffer = buffer;
    whiteNoise.loop = true;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = freq;
    filter.Q.value = q;

    const g = this.ctx.createGain();
    g.gain.value = gain;

    whiteNoise.connect(filter);
    filter.connect(g);
    g.connect(this.presetGain);
    whiteNoise.start();
    this.presetNodes.push(whiteNoise);
  }

  private scheduleCreak() {
    if (!this.ctx || !this.ambientGain) return

    let minDelay = 4000
    let maxDelay = 12000

    // Настройка частоты скрипов в зависимости от пресета
    switch (this.currentPreset) {
      case SoundPreset.CREAK:
        minDelay = 1000; maxDelay = 5000; break
      case SoundPreset.HOUSE:
      case SoundPreset.METAL:
        minDelay = 3000; maxDelay = 10000; break
      case SoundPreset.WIND:
        minDelay = 20000; maxDelay = 60000; break // Почти нет скрипов на улице
      case SoundPreset.VENTILATION:
        minDelay = 10000; maxDelay = 30000; break
    }

    const delay = minDelay + Math.random() * (maxDelay - minDelay)
    this.creakTimeout = setTimeout(() => {
      if (this._muted || !this.ctx || !this.ambientGain) return

      // Creak: short filtered noise sweep
      const duration = 0.15 + Math.random() * 0.3
      const bufferSize = Math.floor(this.ctx.sampleRate * duration)
      const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate)
      const data = buffer.getChannelData(0)
      for (let i = 0; i < bufferSize; i++) {
        const env = Math.sin((i / bufferSize) * Math.PI)
        data[i] = (Math.random() * 2 - 1) * env
      }
      const source = this.ctx.createBufferSource()
      source.buffer = buffer

      const filter = this.ctx.createBiquadFilter()
      filter.type = 'bandpass'

      // В индустриальной зоне скрипы более "металлические" (выше частота)
      const baseFreq = this.currentPreset === SoundPreset.METAL ? 1200 : 600
      filter.frequency.value = baseFreq + Math.random() * 800
      filter.Q.value = 8

      const g = this.ctx.createGain()
      g.gain.value = 0.04 + Math.random() * 0.03

      source.connect(filter)
      filter.connect(g)
      g.connect(this.ambientGain)
      source.start()
      this.scheduleCreak()
    }, delay)
  }

  private scheduleDrip() {
    if (!this.ctx || !this.ambientGain) return

    let minDelay = 3000
    let maxDelay = 8000
    let probability = 1.0

    // Настройка частоты капель в зависимости от пресета
    switch (this.currentPreset) {
      case SoundPreset.DRIP:
        minDelay = 500; maxDelay = 2000; break
      case SoundPreset.ECHO_DRIP:
        minDelay = 1000; maxDelay = 4000; break
      case SoundPreset.WIND:
      case SoundPreset.ELECTRIC:
      case SoundPreset.RESONANCE:
        probability = 0.1; break // Редкие случайные капли
    }

    const delay = minDelay + Math.random() * (maxDelay - minDelay)
    this.dripTimeout = setTimeout(() => {
      if (this._muted || !this.ctx || !this.ambientGain) return

      if (Math.random() > probability) {
        this.scheduleDrip()
        return
      }

      // Drip: short high-pitched sine with fast decay
      const osc = this.ctx.createOscillator()
      const g = this.ctx.createGain()
      osc.type = 'sine'

      // В ванной капли более глухие
      const freqMultiplier = this.currentPreset === SoundPreset.ECHO_DRIP ? 0.6 : 1.0
      const baseFreq = (1200 + Math.random() * 2000) * freqMultiplier

      osc.frequency.setValueAtTime(baseFreq, this.ctx.currentTime)
      osc.frequency.exponentialRampToValueAtTime(baseFreq * 0.5, this.ctx.currentTime + 0.08)

      g.gain.setValueAtTime(0.025, this.ctx.currentTime)
      g.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.12)

      osc.connect(g)
      g.connect(this.ambientGain)
      osc.start()
      osc.stop(this.ctx.currentTime + 0.15)
      this.scheduleDrip()
    }, delay)
  }

  stopAmbient() {
    for (const node of this.ambientNodes) {
      try {
        if (node instanceof OscillatorNode || node instanceof AudioBufferSourceNode) node.stop()
      } catch (_) { /* already stopped */ }
    }
    this.ambientNodes = []

    for (const node of this.presetNodes) {
      try {
        if (node instanceof OscillatorNode || node instanceof AudioBufferSourceNode) node.stop()
      } catch (_) { /* already stopped */ }
    }
    this.presetNodes = []

    if (this.creakTimeout) clearTimeout(this.creakTimeout)
    if (this.dripTimeout) clearTimeout(this.dripTimeout)
    if (this.ambientInterval) clearInterval(this.ambientInterval)
  }

  // ========== GEIGER COUNTER ==========

  startGeiger(radiationLevel: number) {
    this.stopGeiger()
    if (!this.ctx || !this.sfxGain || radiationLevel < 25) return

    const interval = Math.max(80, 1500 - radiationLevel * 15)
    this.geigerInterval = setInterval(() => {
      if (this._muted || !this.ctx || !this.sfxGain) return
      // Single Geiger click
      const clickDuration = 0.01 + Math.random() * 0.02
      const bufferSize = Math.floor(this.ctx.sampleRate * clickDuration)
      const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate)
      const data = buffer.getChannelData(0)
      for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize)
      }
      const source = this.ctx.createBufferSource()
      source.buffer = buffer
      const g = this.ctx.createGain()
      g.gain.value = 0.06 + (radiationLevel / 100) * 0.08
      const filter = this.ctx.createBiquadFilter()
      filter.type = 'highpass'
      filter.frequency.value = 2000
      source.connect(filter)
      filter.connect(g)
      g.connect(this.sfxGain)
      source.start()

      // Sometimes double-click at high radiation
      if (radiationLevel > 60 && Math.random() < 0.3) {
        setTimeout(() => {
          if (this._muted || !this.ctx || !this.sfxGain) return
          const b2size = Math.floor(this.ctx.sampleRate * 0.008)
          const b2 = this.ctx.createBuffer(1, b2size, this.ctx.sampleRate)
          const d2 = b2.getChannelData(0)
          for (let i = 0; i < b2size; i++) {
            d2[i] = (Math.random() * 2 - 1) * (1 - i / b2size)
          }
          const s2 = this.ctx.createBufferSource()
          s2.buffer = b2
          const g2 = this.ctx.createGain()
          g2.gain.value = 0.04
          s2.connect(g2)
          g2.connect(this.sfxGain)
          s2.start()
        }, 30 + Math.random() * 40)
      }
    }, interval)
  }

  stopGeiger() {
    if (this.geigerInterval) {
      clearInterval(this.geigerInterval)
      this.geigerInterval = null
    }
  }

  updateGeiger(radiationLevel: number) {
    if (radiationLevel < 25) {
      this.stopGeiger()
    } else {
      this.startGeiger(radiationLevel)
    }
  }

  // ========== HEARTBEAT (low health) ==========

  startHeartbeat(health: number) {
    this.stopHeartbeat()
    if (!this.ctx || !this.sfxGain || health > 35) return

    const interval = 600 + (health / 35) * 600
    this.heartbeatInterval = setInterval(() => {
      if (this._muted || !this.ctx || !this.sfxGain) return
      // Heartbeat: low thump
      const osc = this.ctx.createOscillator()
      const g = this.ctx.createGain()
      osc.type = 'sine'
      osc.frequency.setValueAtTime(50, this.ctx.currentTime)
      osc.frequency.exponentialRampToValueAtTime(30, this.ctx.currentTime + 0.15)
      g.gain.setValueAtTime(0.12, this.ctx.currentTime)
      g.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.2)
      osc.connect(g)
      g.connect(this.sfxGain)
      osc.start()
      osc.stop(this.ctx.currentTime + 0.25)

      // Second beat (dup)
      setTimeout(() => {
        if (this._muted || !this.ctx || !this.sfxGain) return
        const osc2 = this.ctx.createOscillator()
        const g2 = this.ctx.createGain()
        osc2.type = 'sine'
        osc2.frequency.setValueAtTime(45, this.ctx.currentTime)
        osc2.frequency.exponentialRampToValueAtTime(25, this.ctx.currentTime + 0.1)
        g2.gain.setValueAtTime(0.08, this.ctx.currentTime)
        g2.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.15)
        osc2.connect(g2)
        g2.connect(this.sfxGain)
        osc2.start()
        osc2.stop(this.ctx.currentTime + 0.18)
      }, 150)
    }, interval)
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }

  updateHeartbeat(health: number) {
    if (health > 35) {
      this.stopHeartbeat()
    } else {
      this.startHeartbeat(health)
    }
  }

  // ========== SOUND EFFECTS ==========

  play(soundId: SoundId) {
    if (!this.ctx || !this.sfxGain || this._muted) return
    this.resume()

    switch (soundId) {
      case 'search_rustle':
        this.playSearchRustle()
        break
      case 'search_empty':
        this.playSearchEmpty()
        break
      case 'found_common':
        this.playFoundSound('common')
        break
      case 'found_uncommon':
        this.playFoundSound('uncommon')
        break
      case 'found_rare':
        this.playFoundSound('rare')
        break
      case 'found_legendary':
        this.playFoundSound('legendary')
        break
      case 'use_medical':
        this.playUseMedical()
        break
      case 'use_food':
        this.playUseFood()
        break
      case 'use_protection':
        this.playUseProtection()
        break
      case 'use_artifact':
        this.playUseArtifact()
        break
      case 'use_equip':
        this.playUseEquip()
        break
      case 'door_open':
        this.playDoorOpen()
        break
      case 'door_close':
        this.playDoorClose()
        break
      case 'footstep':
        this.playFootstep()
        break
      case 'ui_click':
        this.playUIClick()
        break
      case 'ui_hover':
        this.playUIHover()
        break
      case 'death':
        this.playDeath()
        break
      case 'zone_hum':
        this.playZoneHum()
        break
      case 'breathing':
        this.playBreathing()
        break;
      case 'ui_error':
        this.playUIError()
        break;
      default:
        break;
    }
  }

  private playUIError() {
    const t = this.ctx!.currentTime;
    const osc = this.ctx!.createOscillator();
    const g = this.ctx!.createGain();

    osc.type = 'square';
    osc.frequency.setValueAtTime(150, t);
    osc.frequency.exponentialRampToValueAtTime(50, t + 0.2);

    g.gain.setValueAtTime(0.1, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.2);

    osc.connect(g);
    g.connect(this.sfxGain!);

    osc.start(t);
    osc.stop(t + 0.2);
  }

  private playSearchRustle() {
    if (!this.ctx || !this.sfxGain) return
    // Rustling/shuffling sound
    this.playNoiseBurst(0.25, 0.08, this.sfxGain, 3000)

    // Small metallic clink
    setTimeout(() => {
      if (!this.ctx || !this.sfxGain) return
      this.playTone(800 + Math.random() * 400, 0.05, 0.03, 'square', this.sfxGain)
    }, 80)

    // Another rustle
    setTimeout(() => {
      this.playNoiseBurst(0.15, 0.05, this.sfxGain, 2500)
    }, 120)
  }

  private playSearchEmpty() {
    if (!this.ctx || !this.sfxGain) return
    // Dull thud - nothing found
    this.playTone(120, 0.2, 0.08, 'sine', this.sfxGain)
    this.playNoiseBurst(0.1, 0.03, this.sfxGain, 800)
  }

  private playFoundSound(rarity: string) {
    if (!this.ctx || !this.sfxGain) return

    switch (rarity) {
      case 'common': {
        // Simple short chime
        this.playTone(600, 0.15, 0.08, 'sine', this.sfxGain)
        setTimeout(() => this.playTone(800, 0.1, 0.06, 'sine', this.sfxGain), 80)
        break
      }
      case 'uncommon': {
        // Two-tone ascending chime
        this.playTone(500, 0.2, 0.08, 'sine', this.sfxGain)
        setTimeout(() => this.playTone(700, 0.15, 0.08, 'sine', this.sfxGain), 100)
        setTimeout(() => this.playTone(900, 0.2, 0.06, 'sine', this.sfxGain), 200)
        break
      }
      case 'rare': {
        // Ethereal shimmer with harmonics
        this.playTone(440, 0.4, 0.06, 'sine', this.sfxGain)
        this.playTone(554, 0.35, 0.05, 'sine', this.sfxGain)
        this.playTone(660, 0.3, 0.05, 'sine', this.sfxGain)
        setTimeout(() => {
          this.playTone(880, 0.5, 0.06, 'sine', this.sfxGain)
          this.playTone(1108, 0.4, 0.04, 'sine', this.sfxGain)
          this.playNoiseBurst(0.3, 0.02, this.sfxGain, 6000)
        }, 150)
        break
      }
      case 'legendary': {
        // Grand artifact discovery - full chord + shimmer
        const freqs = [440, 554, 660, 880]
        freqs.forEach((f, i) => {
          setTimeout(() => {
            this.playTone(f, 0.8, 0.07, 'sine', this.sfxGain)
            this.playTone(f * 2, 0.5, 0.03, 'sine', this.sfxGain)
          }, i * 60)
        })
        // Shimmer
        setTimeout(() => {
          for (let i = 0; i < 5; i++) {
            setTimeout(() => {
              this.playTone(1200 + Math.random() * 800, 0.15, 0.02, 'sine', this.sfxGain)
            }, i * 40)
          }
        }, 300)
        // Low rumble
        this.playTone(60, 1.0, 0.04, 'sine', this.sfxGain)
        break
      }
    }
  }

  private playUseMedical() {
    if (!this.ctx || !this.sfxGain) return
    // Spray/syringe sound
    this.playNoiseBurst(0.15, 0.06, this.sfxGain, 4000)
    setTimeout(() => {
      // Healing tone
      this.playTone(400, 0.3, 0.06, 'sine', this.sfxGain)
      setTimeout(() => this.playTone(600, 0.3, 0.05, 'sine', this.sfxGain), 100)
    }, 150)
  }

  private playUseFood() {
    if (!this.ctx || !this.sfxGain) return
    // Eating/chewing sounds - short noise bursts
    for (let i = 0; i < 3; i++) {
      setTimeout(() => {
        this.playNoiseBurst(0.08, 0.04, this.sfxGain, 1500 + Math.random() * 1000)
      }, i * 120)
    }
    // Satisfied tone
    setTimeout(() => this.playTone(350, 0.15, 0.03, 'sine', this.sfxGain), 400)
  }

  private playUseProtection() {
    if (!this.ctx || !this.sfxGain) return
    // Pill/cap sound + relief
    this.playNoiseBurst(0.05, 0.04, this.sfxGain, 3000)
    setTimeout(() => {
      this.playTone(300, 0.1, 0.04, 'triangle', this.sfxGain)
      setTimeout(() => this.playTone(250, 0.2, 0.05, 'sine', this.sfxGain), 80)
    }, 100)
  }

  private playUseArtifact() {
    if (!this.ctx || !this.sfxGain) return
    // Mystical hum with overtones
    this.playTone(200, 0.6, 0.06, 'sine', this.sfxGain)
    this.playTone(300, 0.5, 0.04, 'sine', this.sfxGain)
    this.playTone(400, 0.4, 0.03, 'sine', this.sfxGain)
    this.playNoiseBurst(0.5, 0.02, this.sfxGain, 5000)
    setTimeout(() => {
      this.playTone(500, 0.4, 0.05, 'sine', this.sfxGain)
      this.playTone(600, 0.3, 0.04, 'sine', this.sfxGain)
    }, 200)
  }

  private playUseEquip() {
    if (!this.ctx || !this.sfxGain) return
    // Metallic clank/adjustment
    this.playTone(200, 0.08, 0.06, 'square', this.sfxGain)
    this.playNoiseBurst(0.1, 0.05, this.sfxGain, 2000)
    setTimeout(() => this.playTone(350, 0.06, 0.04, 'square', this.sfxGain), 60)
    setTimeout(() => this.playNoiseBurst(0.08, 0.03, this.sfxGain, 3000), 100)
  }

  private playDoorOpen() {
    if (!this.ctx || !this.sfxGain) return
    // Creaky door opening - filtered noise sweep up
    const duration = 0.4
    const bufferSize = Math.floor(this.ctx.sampleRate * duration)
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate)
    const data = buffer.getChannelData(0)
    for (let i = 0; i < bufferSize; i++) {
      const t = i / bufferSize
      data[i] = (Math.random() * 2 - 1) * Math.sin(t * Math.PI) * 0.5
    }
    const source = this.ctx.createBufferSource()
    source.buffer = buffer

    const filter = this.ctx.createBiquadFilter()
    filter.type = 'bandpass'
    filter.frequency.setValueAtTime(300, this.ctx.currentTime)
    filter.frequency.exponentialRampToValueAtTime(1200, this.ctx.currentTime + duration)
    filter.Q.value = 5

    const g = this.ctx.createGain()
    g.gain.value = 0.08

    source.connect(filter)
    filter.connect(g)
    g.connect(this.sfxGain)
    source.start()

    // Door latch click
    setTimeout(() => {
      this.playTone(1500, 0.03, 0.04, 'square', this.sfxGain)
    }, 300)
  }

  private playDoorClose() {
    if (!this.ctx || !this.sfxGain) return
    // Door closing - short low thud
    this.playTone(80, 0.15, 0.1, 'sine', this.sfxGain)
    this.playNoiseBurst(0.08, 0.05, this.sfxGain, 1500)
    setTimeout(() => this.playTone(60, 0.1, 0.05, 'sine', this.sfxGain), 50)
  }

  private playFootstep() {
    if (!this.ctx || !this.sfxGain) return
    // Crunchy footstep on debris
    this.playNoiseBurst(0.1, 0.05, this.sfxGain, 1200 + Math.random() * 800)
    this.playTone(80 + Math.random() * 40, 0.08, 0.03, 'sine', this.sfxGain)
  }

  private playUIClick() {
    if (!this.ctx || !this.sfxGain) return
    this.playTone(1000, 0.03, 0.04, 'square', this.sfxGain)
  }

  private playUIHover() {
    if (!this.ctx || !this.sfxGain) return
    this.playTone(800, 0.02, 0.02, 'sine', this.sfxGain)
  }

  private playDeath() {
    if (!this.ctx || !this.sfxGain) return
    // Long descending tone + noise
    const osc = this.ctx.createOscillator()
    const g = this.ctx.createGain()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(400, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(40, this.ctx.currentTime + 2)
    g.gain.setValueAtTime(0.1, this.ctx.currentTime)
    g.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 2.5)
    osc.connect(g)
    g.connect(this.sfxGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 2.5)

    // Heartbeat slowing and stopping
    for (let i = 0; i < 4; i++) {
      setTimeout(() => {
        if (!this.ctx || !this.sfxGain) return
        this.playTone(50 - i * 5, 0.15 - i * 0.02, 0.08 - i * 0.015, 'sine', this.sfxGain)
      }, i * 400)
    }

    // Final noise
    setTimeout(() => {
      this.playNoiseBurst(1.5, 0.04, this.sfxGain, 500)
    }, 500)
  }

  private playZoneHum() {
    if (!this.ctx || !this.sfxGain) return
    // Weird Zone anomaly hum
    const freqs = [73, 110, 146, 220]
    freqs.forEach((f, i) => {
      setTimeout(() => {
        this.playTone(f, 0.5, 0.03, 'sine', this.sfxGain)
      }, i * 50)
    })
  }

  private playBreathing() {
    if (!this.ctx || !this.sfxGain) return
    // Inhale
    this.playNoiseBurst(0.5, 0.03, this.sfxGain, 1500)
    // Exhale
    setTimeout(() => {
      this.playNoiseBurst(0.6, 0.02, this.sfxGain, 1000)
    }, 600)
  }

  // ========== CLEANUP ==========

  destroy() {
    this.stopAmbient()
    this.stopGeiger()
    this.stopHeartbeat()
    if (this.ctx) {
      this.ctx.close()
      this.ctx = null
    }
    this.initialized = false
  }
}

// Singleton instance
export const soundEngine = new StalkerSoundEngine()
